import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Loader2, PlugZap } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError, AuthNotReadyError } from '@/api/client';
import { useMe } from '@/api/queries';
import { useAuth } from '@/auth/AuthProvider';
import { SignInScreen } from '@/auth/SignInScreen';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import { AskPanel, useAskLauncher } from '@/features/ask';
import { AppCommandPalette } from './AppCommandPalette';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';

export function AppShell() {
  const { ready, signedIn, credentialAttached, mode, setDevUser, signOut } = useAuth();
  const me = useMe(signedIn);
  const unauthorised = me.error instanceof ApiError && me.error.isAuth;
  const notReadyYet = me.error instanceof AuthNotReadyError;
  // One launcher for the whole shell: the topbar button and the ⌘K entry open the same panel.
  const ask = useAskLauncher();

  useEffect(() => {
    if (unauthorised && mode === 'dev') setDevUser(null);
  }, [unauthorised, mode, setDevUser]);

  useEffect(() => {
    if (me.error && !(me.error instanceof AuthNotReadyError) && !(me.error instanceof ApiError && (me.error.isAuth || me.error.isMissing))) {
      toast.error('Could not load your profile', { description: me.error.message });
    }
  }, [me.error]);

  // MSAL has not finished deciding who is signed in: showing the sign-in screen here is what
  // made a signed-in user bounce, so wait instead.
  if (!ready) return <ConnectingScreen />;
  if (!signedIn) return <SignInScreen />;

  // A 401 that arrived WITH a credential is a real refusal (the account is not provisioned in
  // CIGS). A failure without one is a timing problem, and retrying is the right answer.
  if (unauthorised && credentialAttached && mode === 'entra') {
    return <NotProvisionedScreen message={me.error instanceof Error ? me.error.message : ''} onRetry={() => void me.refetch()} onSignOut={signOut} />;
  }
  if (notReadyYet) return <ConnectingScreen />;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <SideNav me={me.data} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar me={me.data} loading={me.isLoading} onAsk={ask.launch} />
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
      <AppCommandPalette me={me.data} onAsk={ask.launch} />
      <AskPanel {...ask} />
    </div>
  );
}

function ConnectingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  );
}

function NotProvisionedScreen({
  message,
  onRetry,
  onSignOut,
}: {
  message: string;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
      <EmptyState
        icon={<PlugZap className="h-6 w-6" aria-hidden />}
        title="Signed in, but CIGS refused the request"
        description={
          message
            ? `Your Beeline account is signed in, but the API rejected it: ${message}. Ask the CIGS administrator to add you as a user.`
            : 'Your Beeline account is signed in, but the API rejected it. Ask the CIGS administrator to add you as a user.'
        }
        action={{ label: 'Try again', onClick: onRetry }}
      />
      <Button variant="ghost" size="sm" onClick={onSignOut}>
        Sign in as someone else
      </Button>
    </div>
  );
}
